import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { getAccessToken } from "../lib/supabaseClient";
import { SBLogo } from "../components/WebLayout";
import Seo from "../components/Seo";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, ArrowRight, CheckCircle2, Check, Edit3, Save, RefreshCw,
  Camera, Trash2, Upload, ClipboardList, X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

async function af(path, opts = {}) {
  const t = await getAccessToken();
  return fetch(`${API}${path}`, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${t || ""}` } });
}

const STEPS = [
  { key: "location",   label: "Business & Location", subtitle: "Where is this vending opportunity?" },
  { key: "traffic",    label: "Traffic & Hours",     subtitle: "Who's on-site and when?" },
  { key: "amenities",  label: "Site & Power",        subtitle: "What's already there?" },
  { key: "manager",    label: "Manager Contact",     subtitle: "Who authorizes vending here?" },
  { key: "placement",  label: "Placement Notes",     subtitle: "Where would the machine go?" },
  { key: "preference", label: "Machines & Terms",    subtitle: "What setup and terms?" },
  { key: "terms",      label: "General Notes",       subtitle: "Anything else?" },
  { key: "checklist",  label: "Field Checklist",     subtitle: "On-site quick reference." },
  { key: "photos",     label: "Photos",              subtitle: "Capture the site." },
  { key: "review",     label: "Review & Submit",     subtitle: "Confirm and send." },
];

const MACHINE_TYPES = ["Snack", "Drink", "Combo", "Smart Cooler", "Coffee", "Micro Market"];
const OPP_TYPES = ["snack","beverage","combo","healthy","coffee","smart_cooler","micro_market","other"];
const EXISTING_MACHINE_TYPES = ["snack","drink","combo","fresh_food","coffee","smart_cooler","other"];

const YES_NO_UNKNOWN = [["yes","Yes"],["no","No"],["unknown","Unknown"]];
const CONDITION = [["modern","Modern"],["average","Average"],["old","Old"],["unknown","Unknown"]];
const WEEKEND = [["busy","Busy"],["moderate","Moderate"],["quiet","Quiet"],["closed","Closed"],["unknown","Unknown"]];
const TRAFFIC_TIER = [["low","Low"],["medium","Medium"],["high","High"],["very_high","Very High"]];
const VENDING_STATE = [["none","None"],["exists","Exists"],["unknown","Unknown"]];
const INDOOR_OUT = [["indoor","Indoor"],["outdoor","Outdoor"],["both","Both"]];
const SIGNAL = [["good","Good"],["poor","Poor"],["unknown","Unknown"]];
const DM_CONTACT = [["phone","Phone"],["email","Email"],["text","Text"],["in_person","In-person"]];
const DM_INTEREST = [["not_contacted","Not contacted"],["contacted","Contacted"],["interested","Interested"],
                     ["very_interested","Very interested"],["needs_follow_up","Needs follow-up"],["declined","Declined"]];
const FIN_PREF = [["no_commission","No commission"],["fixed_rent","Fixed rent"],["revenue_share","Revenue share"],["negotiable","Negotiable"]];
const EXCLUSIVITY = [["requested","Requested"],["accepted","Accepted"],["declined","Declined"],["unknown","Unknown"]];
const TERM_MONTHS = [12, 24, 36, 48, 60];

const PHOTO_CATEGORIES = [
  { key: "exterior",           label: "Exterior",           required: true  },
  { key: "entrance",           label: "Entrance",           required: true  },
  { key: "placement_area",     label: "Placement Area",     required: true  },
  { key: "foot_traffic_view",  label: "Foot-Traffic View",  required: true  },
  { key: "existing_vending",   label: "Existing Vending",   required: false },
  { key: "common_area",        label: "Common Area",        required: false },
  { key: "electrical_outlet",  label: "Electrical Outlet",  required: false },
  { key: "parking_entrance",   label: "Parking / Entrance", required: false },
  { key: "other",              label: "Other",              required: false },
];

const CHECKLIST_LOOK_AROUND = [
  ["vending_exists", "Is there already vending here?"],
  ["existing_old_stocked", "Are existing machines old or poorly stocked?"],
  ["heavy_traffic", "Is there heavy employee/resident/customer traffic?"],
  ["onsite_long", "Are people normally onsite for long periods?"],
  ["food_drink_inconvenient", "Is food/drink nearby or inconvenient?"],
  ["visible_placement", "Is there a good visible placement area?"],
  ["power_nearby", "Is electrical power nearby?"],
  ["placement_secure", "Is the machine location secure?"],
];
const CHECKLIST_TALK_TO = [
  "Owner", "General Manager", "Property Manager", "Facility Manager",
  "Office Manager", "HR Manager", "Operations Manager",
];
const CHECKLIST_QUESTIONS = [
  "Do you currently have vending machines?",
  "Are you happy with the current service?",
  "Approximately how many employees/residents/customers use this location?",
  "Would you be interested in modern snack/drink/smart vending?",
  "Would you expect a commission or prefer a free amenity?",
  "Where would you want the machine placed?",
  "Would you be comfortable with a 12/24/36/48+ month placement agreement?",
  "Are you the person authorized to approve it?",
];

export default function ScoutFindLocation() {
  const { id } = useParams();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [loc, setLoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error
  const [savedAt, setSavedAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const saveTimer = useRef(null);
  const pendingPatch = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    const r = await af(`/api/scouts/locations/${id}`);
    if (r.status === 401 || r.status === 403) { nav("/scouts/login"); return; }
    if (!r.ok) { setError("Location not found"); setLoading(false); return; }
    const d = await r.json();
    setLoc(d.location); setLoading(false);
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  const readOnly = loc && !(loc.status === "draft" || loc.status === "more_info_requested");

  function set(k, v) {
    setLoc((l) => ({ ...l, [k]: v }));
    pendingPatch.current[k] = v;
    scheduleSave();
  }
  function scheduleSave() {
    if (readOnly) return;
    setSaveState("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(doSave, 1500);
  }
  async function doSave(retry = false) {
    if (readOnly) return;
    const patch = { ...pendingPatch.current };
    if (Object.keys(patch).length === 0) { setSaveState("saved"); return; }
    setSaveState("saving");
    try {
      const r = await af(`/api/scouts/locations/${id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(patch),
      });
      if (r.status === 403) {
        const d = await r.json().catch(() => ({}));
        if (d?.detail === "email_not_verified") { toast.error("Verify your email to save changes"); setSaveState("error"); return; }
      }
      if (!r.ok) { setSaveState("error"); return; }
      pendingPatch.current = {};
      setSaveState("saved");
      setSavedAt(new Date());
    } catch { setSaveState("error"); }
  }
  async function retrySave() { await doSave(true); }

  async function submit() {
    setSubmitting(true); setError("");
    // Flush any pending save first
    if (Object.keys(pendingPatch.current).length) await doSave();
    const r = await af(`/api/scouts/locations/${id}/submit`, { method: "POST" });
    const d = await r.json().catch(() => ({}));
    setSubmitting(false);
    if (!r.ok) {
      if (d.missing) setError(`Missing: ${d.missing.join(", ")}`);
      else if (d.detail === "email_not_verified") setError("Please verify your email before submitting.");
      else setError(d.detail || "Could not submit");
      return;
    }
    toast.success("Location submitted");
    nav("/scouts/dashboard");
  }

  const stepMeta = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const saveIndicator = useMemo(() => {
    if (readOnly) return { text: "Read-only", tone: "text-[#A1A1AA]" };
    if (saveState === "saving") return { text: "Saving…", tone: "text-[#A1A1AA]" };
    if (saveState === "error")  return { text: "Unable to save", tone: "text-[#EF4444]", showRetry: true };
    if (saveState === "saved" && savedAt) return { text: `Saved · ${savedAt.toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}`, tone: "text-[#22C55E]" };
    if (saveState === "saved") return { text: "Saved", tone: "text-[#22C55E]" };
    return { text: "", tone: "text-[#A1A1AA]" };
  }, [saveState, readOnly, savedAt]);

  if (loading || !loc) {
    return (
      <div className="min-h-screen bg-[#080B0D] text-white flex items-center justify-center">
        <Loader2 size={18} className="animate-spin text-[#FFC107]" />
      </div>
    );
  }

  const cs = loc.field_checklist_state || {};
  function updateChecklist(section, key, val) {
    const next = { ...cs, [section]: { ...(cs[section] || {}), [key]: val } };
    set("field_checklist_state", next);
  }

  return (
    <div className="min-h-screen bg-[#080B0D] text-white">
      <Seo title="Find Location" description="SpotBounty scout location submission" path={`/scouts/find-location/${id}`} noindex />

      <header className="border-b border-[#2A2F34] sticky top-0 z-10 bg-[#080B0D]/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/scouts/dashboard" className="flex items-center gap-2 text-[12px] text-[#A1A1AA] hover:text-white" data-testid="scout-back">
              <ArrowLeft size={13} /> My Locations
            </Link>
            <div className="hidden sm:block h-4 w-px bg-[#2A2F34]" />
            <SBLogo size={16} />
          </div>
          <div className="flex items-center gap-3 text-[11px]">
            <div className="lg:hidden text-[#A1A1AA]">Step <b className="text-white">{step + 1}</b> of {STEPS.length}</div>
            <div className={`flex items-center gap-1.5 ${saveIndicator.tone}`} data-testid="scout-save-indicator">
              {saveState === "saving" ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
              <span>{saveIndicator.text}</span>
              {saveIndicator.showRetry && (
                <button onClick={retrySave} className="ml-1 text-[#FFC107] hover:underline flex items-center gap-1" data-testid="scout-save-retry">
                  <RefreshCw size={10} /> Retry
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="lg:hidden px-4 pb-3">
          <div className="flex gap-1">
            {STEPS.map((_, i) => <div key={i} className={`flex-1 h-1 rounded ${i <= step ? "bg-[#FFC107]" : "bg-[#2A2F34]"}`} />)}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6 grid lg:grid-cols-[280px,1fr] gap-8">
        <aside className="hidden lg:block sticky top-24 self-start">
          <div className="sb-eyebrow">Progress</div>
          <ol className="mt-4 space-y-1" data-testid="scout-step-rail">
            {STEPS.map((s, i) => {
              const active = i === step, done = i < step;
              return (
                <li key={s.key}>
                  <button onClick={() => setStep(i)}
                          className={`w-full text-left flex gap-3 items-start px-3 py-2.5 rounded-lg transition-colors ${active ? "bg-[#FFC107]/10" : "hover:bg-white/[.03]"}`}
                          data-testid={`scout-rail-${s.key}`}>
                    <div className={`w-7 h-7 rounded-full border flex items-center justify-center text-[11px] font-black shrink-0 ${
                      active ? "border-[#FFC107] text-[#FFC107]" : done ? "border-[#22C55E] text-[#22C55E]" : "border-[#2A2F34] text-[#52525b]"
                    }`}>{done ? <Check size={13} /> : i + 1}</div>
                    <div className="min-w-0">
                      <div className={`text-[12.5px] font-bold ${active ? "text-[#FFC107]" : done ? "text-white" : "text-[#A1A1AA]"}`}>{s.label}</div>
                      <div className="text-[10.5px] text-[#52525b] mt-0.5">{s.subtitle}</div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>

        <div className="min-w-0 space-y-5">
          {readOnly && (
            <div className="sb-card p-4 border-[#3B82F6]/40" style={{ background: "rgba(59,130,246,.05)" }}>
              <div className="text-[13px]">This location is <b>{loc.status}</b>. It&apos;s read-only unless SpotBounty requests more info.</div>
            </div>
          )}

          <div>
            <div className="sb-eyebrow">Step {step + 1} of {STEPS.length}</div>
            <h1 className="mt-1 sb-headline text-[26px] md:text-[32px]">{stepMeta.label.toUpperCase()}</h1>
            <div className="text-[13px] text-[#A1A1AA] mt-1">{stepMeta.subtitle}</div>
          </div>

          <section className="sb-card p-5 md:p-6 space-y-4">
            {step === 0 && <StepLocation loc={loc} set={set} readOnly={readOnly} />}
            {step === 1 && <StepTraffic  loc={loc} set={set} readOnly={readOnly} />}
            {step === 2 && <StepAmenities loc={loc} set={set} readOnly={readOnly} />}
            {step === 3 && <StepManager  loc={loc} set={set} readOnly={readOnly} />}
            {step === 4 && <StepPlacement loc={loc} set={set} readOnly={readOnly} />}
            {step === 5 && <StepPreference loc={loc} set={set} readOnly={readOnly} />}
            {step === 6 && <StepGeneralNotes loc={loc} set={set} readOnly={readOnly} />}
            {step === 7 && <StepChecklist cs={cs} update={updateChecklist} readOnly={readOnly} />}
            {step === 8 && <StepPhotos locId={id} readOnly={readOnly} onChange={(cats) => {
              // Auto-check the checklist "photos" section as photos of a category get added
              const next = { ...cs, photos: cats };
              set("field_checklist_state", next);
            }} initialCategories={cs.photos || {}} />}
            {step === 9 && <ReviewStep loc={loc} readOnly={readOnly} onEdit={(i) => setStep(i)} onSubmit={submit} submitting={submitting} error={error} />}
          </section>

          {!isLast && (
            <div className="flex justify-between items-center gap-3">
              <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
                      className="sb-btn-secondary text-[12px] disabled:opacity-40" style={{ padding: "12px 18px" }} data-testid="w-back">
                <ArrowLeft size={13} /> Back
              </button>
              <div className="flex gap-2">
                {!readOnly && (
                  <button onClick={() => doSave()} className="sb-btn-secondary text-[12px]" style={{ padding: "12px 16px" }} data-testid="w-save-draft">
                    <Save size={13} /> Save Draft
                  </button>
                )}
                <button onClick={() => setStep(Math.min(STEPS.length - 1, step + 1))}
                        className="sb-btn-primary text-[12px]" style={{ padding: "12px 20px" }} data-testid="w-next">
                  Next <ArrowRight size={13} />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

// ---------- Step components (compact) ----------

function StepLocation({ loc, set, readOnly }) {
  return (
    <>
      <F label="Business name *"><input disabled={readOnly} className="sb-input" value={loc.business_name || ""} onChange={(e) => set("business_name", e.target.value)} data-testid="w-business-name" /></F>
      <F label="Business type"><input disabled={readOnly} className="sb-input" value={loc.business_type || ""} onChange={(e) => set("business_type", e.target.value)} placeholder="Warehouse, Office, Gym…" /></F>
      <F label="Address *"><input disabled={readOnly} className="sb-input" value={loc.address_line1 || ""} onChange={(e) => set("address_line1", e.target.value)} data-testid="w-address1" /></F>
      <div className="grid grid-cols-2 gap-3">
        <F label="Suite / Unit"><input disabled={readOnly} className="sb-input" value={loc.suite_unit || ""} onChange={(e) => set("suite_unit", e.target.value)} /></F>
        <F label="Address line 2"><input disabled={readOnly} className="sb-input" value={loc.address_line2 || ""} onChange={(e) => set("address_line2", e.target.value)} /></F>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <F label="City *"><input disabled={readOnly} className="sb-input" value={loc.city || ""} onChange={(e) => set("city", e.target.value)} data-testid="w-city" /></F>
        <F label="State *"><input disabled={readOnly} className="sb-input uppercase" maxLength={2} value={loc.state || ""} onChange={(e) => set("state", e.target.value.toUpperCase())} data-testid="w-state" /></F>
        <F label="ZIP"><input disabled={readOnly} className="sb-input" value={loc.zip || ""} onChange={(e) => set("zip", e.target.value)} /></F>
      </div>
      <F label="Google Maps address (optional)"><input disabled={readOnly} className="sb-input" value={loc.google_maps_address || ""} onChange={(e) => set("google_maps_address", e.target.value)} placeholder="Paste from Google Maps if useful" /></F>
    </>
  );
}

function StepTraffic({ loc, set, readOnly }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <F label="Employees"><input disabled={readOnly} type="number" className="sb-input" value={loc.employees ?? ""} onChange={(e) => set("employees", e.target.value ? parseInt(e.target.value, 10) : null)} /></F>
        <F label="Est. residents"><input disabled={readOnly} type="number" className="sb-input" value={loc.est_residents ?? ""} onChange={(e) => set("est_residents", e.target.value ? parseInt(e.target.value, 10) : null)} /></F>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <F label="Daily visitors"><input disabled={readOnly} type="number" className="sb-input" value={loc.visitors ?? ""} onChange={(e) => set("visitors", e.target.value ? parseInt(e.target.value, 10) : null)} /></F>
        <F label="Shifts (1, 2 or 3)"><input disabled={readOnly} type="number" min="1" max="3" className="sb-input" value={loc.shifts ?? ""} onChange={(e) => set("shifts", e.target.value ? parseInt(e.target.value, 10) : null)} /></F>
      </div>
      <F label="Hours"><input disabled={readOnly} className="sb-input" value={loc.hours || ""} onChange={(e) => set("hours", e.target.value)} placeholder="e.g. Mon-Fri 8am-6pm" /></F>
      <label className="flex items-center gap-2 text-[13px]"><input disabled={readOnly} type="checkbox" checked={!!loc.open_24_7} onChange={(e) => set("open_24_7", e.target.checked)} /> Open 24/7</label>
      <Picker label="Foot-traffic tier"      options={TRAFFIC_TIER} value={loc.foot_traffic_tier} onChange={(v) => set("foot_traffic_tier", v)} readOnly={readOnly} />
      <Picker label="Weekend activity"       options={WEEKEND}      value={loc.weekend_activity}  onChange={(v) => set("weekend_activity",  v)} readOnly={readOnly} />
    </>
  );
}

function StepAmenities({ loc, set, readOnly }) {
  return (
    <>
      <Picker label="Vending currently on-site" options={VENDING_STATE} value={loc.vending_state} onChange={(v) => set("vending_state", v)} readOnly={readOnly} />
      <MultiChip label="Existing machine types" options={EXISTING_MACHINE_TYPES} value={loc.existing_machine_types || []} onChange={(v) => set("existing_machine_types", v)} readOnly={readOnly} />
      <Picker label="Existing machine condition" options={CONDITION} value={loc.existing_machine_condition} onChange={(v) => set("existing_machine_condition", v)} readOnly={readOnly} />
      <Picker label="Existing machines well-stocked?" options={YES_NO_UNKNOWN} value={loc.existing_machine_stocked} onChange={(v) => set("existing_machine_stocked", v)} readOnly={readOnly} />
      <Picker label="Existing accepts card?" options={YES_NO_UNKNOWN} value={loc.existing_card_payment} onChange={(v) => set("existing_card_payment", v)} readOnly={readOnly} />
      <F label="Existing vending notes"><textarea disabled={readOnly} rows={2} className="sb-input" value={loc.existing_vending_notes || ""} onChange={(e) => set("existing_vending_notes", e.target.value)} /></F>
      <div className="h-px bg-[#2A2F34]" />
      <Picker label="Wi-Fi available" options={YES_NO_UNKNOWN} value={loc.wifi_available} onChange={(v) => set("wifi_available", v)} readOnly={readOnly} />
      <Picker label="Cellular signal"  options={SIGNAL} value={loc.cellular_signal} onChange={(v) => set("cellular_signal", v)} readOnly={readOnly} />
      <Picker label="Indoor / Outdoor" options={INDOOR_OUT} value={loc.indoor_outdoor} onChange={(v) => set("indoor_outdoor", v)} readOnly={readOnly} />
      <label className="flex items-center gap-2 text-[13px]"><input disabled={readOnly} type="checkbox" checked={!!loc.secure_after_hours} onChange={(e) => set("secure_after_hours", e.target.checked)} /> Secure after hours</label>
      <label className="flex items-center gap-2 text-[13px]"><input disabled={readOnly} type="checkbox" checked={!!loc.outlet_available} onChange={(e) => set("outlet_available", e.target.checked)} /> Power outlet available at the proposed spot</label>
      <F label="Outlet / power notes"><textarea disabled={readOnly} rows={2} className="sb-input" value={loc.outlet_notes || ""} onChange={(e) => set("outlet_notes", e.target.value)} /></F>
      <div className="grid grid-cols-2 gap-3">
        <F label="Available width (in)"><input disabled={readOnly} type="number" className="sb-input" value={loc.available_width_in ?? ""} onChange={(e) => set("available_width_in", e.target.value ? parseFloat(e.target.value) : null)} /></F>
        <F label="Available depth (in)"><input disabled={readOnly} type="number" className="sb-input" value={loc.available_depth_in ?? ""} onChange={(e) => set("available_depth_in", e.target.value ? parseFloat(e.target.value) : null)} /></F>
      </div>
    </>
  );
}

function StepManager({ loc, set, readOnly }) {
  return (
    <>
      <F label="Manager / decision-maker name"><input disabled={readOnly} className="sb-input" value={loc.manager_name || ""} onChange={(e) => set("manager_name", e.target.value)} data-testid="w-mgr-name" /></F>
      <div className="grid grid-cols-2 gap-3">
        <F label="Title"><input disabled={readOnly} className="sb-input" value={loc.manager_title || ""} onChange={(e) => set("manager_title", e.target.value)} /></F>
        <F label="Phone"><input disabled={readOnly} className="sb-input" value={loc.manager_phone || ""} onChange={(e) => set("manager_phone", e.target.value)} /></F>
      </div>
      <F label="Email"><input disabled={readOnly} type="email" className="sb-input" value={loc.manager_email || ""} onChange={(e) => set("manager_email", e.target.value)} data-testid="w-mgr-email" /></F>
      <F label="Best contact time"><input disabled={readOnly} className="sb-input" value={loc.best_contact_time || ""} onChange={(e) => set("best_contact_time", e.target.value)} /></F>
      <div className="h-px bg-[#2A2F34]" />
      <label className="flex items-center gap-2 text-[13px]"><input disabled={readOnly} type="checkbox" checked={!!loc.dm_was_spoken_to} onChange={(e) => set("dm_was_spoken_to", e.target.checked)} /> I spoke with the decision maker</label>
      <Picker label="Preferred contact method" options={DM_CONTACT}  value={loc.dm_preferred_contact} onChange={(v) => set("dm_preferred_contact", v)} readOnly={readOnly} />
      <Picker label="Decision-maker interest"   options={DM_INTEREST} value={loc.dm_interest_level}    onChange={(v) => set("dm_interest_level",    v)} readOnly={readOnly} />
    </>
  );
}

function StepPlacement({ loc, set, readOnly }) {
  return <F label="Placement notes"><textarea disabled={readOnly} rows={6} className="sb-input" value={loc.placement_notes || ""} onChange={(e) => set("placement_notes", e.target.value)} placeholder="Where exactly could the machine go? Foot traffic, break room layout, etc." /></F>;
}

function StepPreference({ loc, set, readOnly }) {
  const fp = loc.financial_pref;
  return (
    <>
      <div>
        <div className="sb-label">Desired machine types</div>
        <div className="flex flex-wrap gap-2">
          {MACHINE_TYPES.map((m) => {
            const active = (loc.desired_machine_types || []).includes(m);
            return (
              <button key={m} type="button" disabled={readOnly} onClick={() => {
                const cur = loc.desired_machine_types || [];
                set("desired_machine_types", active ? cur.filter((x) => x !== m) : [...cur, m]);
              }} className={`text-[11px] font-bold px-3 py-2 rounded-lg border transition-colors ${active ? "border-[#FFC107] text-[#FFC107] bg-[#FFC107]/10" : "border-[#2A2F34] text-[#A1A1AA] hover:text-white"}`}>{m}</button>
            );
          })}
        </div>
      </div>
      <MultiChip label="Preferred opportunity types" options={OPP_TYPES} value={loc.preferred_opportunity_types || []} onChange={(v) => set("preferred_opportunity_types", v)} readOnly={readOnly} />

      <Picker label="Financial preference" options={FIN_PREF} value={fp} onChange={(v) => set("financial_pref", v)} readOnly={readOnly} />
      {/* Fix 3: conditional inputs — only surface the one that matches the financial_pref */}
      {(fp === "revenue_share" || fp === "negotiable") && (
        <F label="Requested commission %"><input disabled={readOnly} type="number" step="0.1" className="sb-input" value={loc.requested_commission_pct ?? ""} onChange={(e) => set("requested_commission_pct", e.target.value ? parseFloat(e.target.value) : null)} data-testid="w-pct" /></F>
      )}
      {(fp === "fixed_rent" || fp === "negotiable") && (
        <F label="Requested monthly $"><input disabled={readOnly} type="number" className="sb-input" value={loc.requested_monthly_amount ?? ""} onChange={(e) => set("requested_monthly_amount", e.target.value ? parseFloat(e.target.value) : null)} data-testid="w-monthly" /></F>
      )}

      <div>
        <div className="sb-label">Preferred term (months)</div>
        <div className="flex flex-wrap gap-2">
          {TERM_MONTHS.map((m) => {
            const active = loc.preferred_term_months === m;
            return (
              <button key={m} type="button" disabled={readOnly} onClick={() => set("preferred_term_months", active ? null : m)}
                      className={`text-[11px] font-bold px-3 py-2 rounded-lg border transition-colors ${active ? "border-[#FFC107] text-[#FFC107] bg-[#FFC107]/10" : "border-[#2A2F34] text-[#A1A1AA] hover:text-white"}`}
                      data-testid={`w-term-${m}`}>{m}</button>
            );
          })}
        </div>
        <F label="Custom term (if not standard)"><input disabled={readOnly} className="sb-input" value={loc.preferred_term_custom || ""} onChange={(e) => set("preferred_term_custom", e.target.value)} placeholder="e.g. Month-to-month" /></F>
      </div>
      <Picker label="Exclusivity preference" options={EXCLUSIVITY} value={loc.exclusivity_pref} onChange={(v) => set("exclusivity_pref", v)} readOnly={readOnly} />
      <label className="flex items-center gap-2 text-[13px]"><input disabled={readOnly} type="checkbox" checked={!!loc.owner_willing_to_sign_now} onChange={(e) => set("owner_willing_to_sign_now", e.target.checked)} /> Owner is willing to sign now</label>
    </>
  );
}

function StepGeneralNotes({ loc, set, readOnly }) {
  return <F label="General notes"><textarea disabled={readOnly} rows={7} className="sb-input" value={loc.general_notes || ""} onChange={(e) => set("general_notes", e.target.value)} placeholder="Any other context the SpotBounty team should know" /></F>;
}

function StepChecklist({ cs, update, readOnly }) {
  return (
    <div className="space-y-6">
      <div>
        <div className="sb-eyebrow">Look around</div>
        <div className="text-[11.5px] text-[#52525b] mt-1">Reference only — tick as you observe.</div>
        <div className="mt-3 space-y-2">
          {CHECKLIST_LOOK_AROUND.map(([k, q]) => (
            <label key={k} className="flex items-center gap-3 text-[13px] py-1.5">
              <input disabled={readOnly} type="checkbox" checked={!!(cs.look || {})[k]} onChange={(e) => update("look", k, e.target.checked)} data-testid={`chk-look-${k}`} />
              {q}
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className="sb-eyebrow">Talk to</div>
        <div className="text-[11.5px] text-[#52525b] mt-1">Who to ask on-site.</div>
        <div className="mt-3 flex flex-wrap gap-2">
          {CHECKLIST_TALK_TO.map((role) => <span key={role} className="text-[11.5px] px-3 py-1.5 rounded-full border border-[#2A2F34] text-[#A1A1AA]">{role}</span>)}
        </div>
      </div>
      <div>
        <div className="sb-eyebrow">Quick questions to ask</div>
        <div className="text-[11.5px] text-[#52525b] mt-1">Reference — no need to record answers here.</div>
        <ol className="mt-3 space-y-2 text-[13px] list-decimal pl-5 text-[#A1A1AA]">
          {CHECKLIST_QUESTIONS.map((q) => <li key={q}>{q}</li>)}
        </ol>
      </div>
    </div>
  );
}

// ---------- Photos step ----------
function StepPhotos({ locId, readOnly, onChange, initialCategories }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [chosenCategory, setChosenCategory] = useState("exterior");

  const load = useCallback(async () => {
    setLoading(true);
    const r = await af(`/api/scouts/locations/${locId}/photos`);
    if (r.ok) { const d = await r.json(); setRows(d.rows || []); }
    setLoading(false);
  }, [locId]);
  useEffect(() => { load(); }, [load]);

  // Auto-check checklist when categories change
  useEffect(() => {
    const has = {};
    for (const r of rows) has[r.category] = true;
    onChange(has);
  }, [rows.length]); // eslint-disable-line react-hooks/exhaustive-deps

  async function pickAndUpload(file) {
    if (!file || readOnly) return;
    setUploading(true); setProgress(0);
    try {
      // Client-side compression / EXIF-safe resize if >2MB
      let out = file;
      if (file.size > 2 * 1024 * 1024) {
        try {
          const mod = await import("browser-image-compression");
          out = await mod.default(file, { maxSizeMB: 2, maxWidthOrHeight: 2000, useWebWorker: true, initialQuality: 0.85 });
        } catch { /* fallback: send as-is */ }
      }
      const ext = (out.name || file.name || "photo.jpg").split(".").pop().toLowerCase();
      const mime = out.type || file.type || "image/jpeg";
      // 1) get signed URL
      const su = await af(`/api/scouts/locations/${locId}/photos/upload-url`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mime, ext }),
      });
      if (!su.ok) { toast.error("Could not get upload URL"); setUploading(false); return; }
      const { upload_url, storage_path } = await su.json();
      // 2) PUT the file
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", upload_url);
        xhr.setRequestHeader("Content-Type", mime);
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100)); };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error("upload_" + xhr.status));
        xhr.onerror = () => reject(new Error("net"));
        xhr.send(out);
      });
      // 3) register
      const reg = await af(`/api/scouts/locations/${locId}/photos`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category: chosenCategory, storage_path, mime, size_bytes: out.size }),
      });
      if (!reg.ok) { toast.error("Upload succeeded but could not save metadata"); }
      else toast.success(`Photo added: ${chosenCategory.replace("_"," ")}`);
      await load();
    } catch (e) {
      toast.error("Upload failed");
    }
    setUploading(false); setProgress(0);
  }

  async function del(pid) {
    if (readOnly) return;
    if (!window.confirm("Delete this photo?")) return;
    const r = await af(`/api/scouts/locations/${locId}/photos/${pid}`, { method: "DELETE" });
    if (r.ok) { toast.success("Photo deleted"); await load(); }
  }

  async function patchCaption(pid, caption) {
    await af(`/api/scouts/locations/${locId}/photos/${pid}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ caption }),
    });
  }

  const requiredMissing = PHOTO_CATEGORIES.filter((c) => c.required && !rows.some((r) => r.category === c.key));

  return (
    <div className="space-y-5">
      {requiredMissing.length > 0 && (
        <div className="text-[12px] text-[#F59E0B] flex items-start gap-2">
          <ClipboardList size={13} className="mt-0.5" />
          <div>
            Missing recommended photos: {requiredMissing.map((c) => c.label).join(", ")}.
            You can still submit — these help SpotBounty verify faster.
          </div>
        </div>
      )}

      {!readOnly && (
        <div className="sb-card p-4">
          <div className="sb-eyebrow">Add photo</div>
          <div className="mt-3">
            <div className="sb-label">Category</div>
            <div className="flex flex-wrap gap-2">
              {PHOTO_CATEGORIES.map((c) => {
                const active = chosenCategory === c.key;
                return (
                  <button key={c.key} type="button" onClick={() => setChosenCategory(c.key)}
                          className={`text-[11px] font-bold px-3 py-2 rounded-lg border transition-colors ${active ? "border-[#FFC107] text-[#FFC107] bg-[#FFC107]/10" : "border-[#2A2F34] text-[#A1A1AA] hover:text-white"}`}
                          data-testid={`photo-cat-${c.key}`}>
                    {c.label} {c.required && <span className="text-[#F59E0B] ml-1">*</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <label className="sb-btn-secondary text-[12px] cursor-pointer justify-center" style={{ padding: "12px 16px" }} data-testid="photo-camera">
              <Camera size={13} /> Take Photo
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => pickAndUpload(e.target.files?.[0])} disabled={uploading} />
            </label>
            <label className="sb-btn-secondary text-[12px] cursor-pointer justify-center" style={{ padding: "12px 16px" }} data-testid="photo-library">
              <Upload size={13} /> Photo Library
              <input type="file" accept="image/*" className="hidden" onChange={(e) => pickAndUpload(e.target.files?.[0])} disabled={uploading} />
            </label>
          </div>

          {uploading && (
            <div className="mt-3">
              <div className="text-[11.5px] text-[#A1A1AA] flex items-center gap-2"><Loader2 size={11} className="animate-spin" /> Uploading… {progress}%</div>
              <div className="mt-1 h-1 rounded bg-[#2A2F34] overflow-hidden"><div className="h-full bg-[#FFC107] transition-all" style={{ width: `${progress}%` }} /></div>
            </div>
          )}
        </div>
      )}

      <div className="grid gap-3">
        {loading && <div className="text-[#A1A1AA] flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading photos…</div>}
        {!loading && rows.length === 0 && <div className="text-[13px] text-[#52525b]">No photos yet.</div>}
        {rows.map((p) => (
          <div key={p.id} className="sb-card p-3 flex gap-3">
            <div className="w-24 h-24 rounded-lg overflow-hidden bg-[#0B0E11] border border-[#2A2F34] shrink-0 flex items-center justify-center">
              {p.download_url ? <img src={p.download_url} alt="" className="w-full h-full object-cover" /> : <Camera size={20} className="text-[#52525b]" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[12px] font-bold uppercase tracking-widest text-[#FFC107]">{p.category?.replace(/_/g," ") || "Uncategorised"}</div>
                {!readOnly && (
                  <button onClick={() => del(p.id)} className="text-[11px] text-[#EF4444] hover:underline flex items-center gap-1" data-testid={`photo-del-${p.id}`}>
                    <Trash2 size={11} /> Delete
                  </button>
                )}
              </div>
              <input disabled={readOnly} defaultValue={p.caption || ""} onBlur={(e) => patchCaption(p.id, e.target.value)}
                     placeholder="Add a short caption…" className="sb-input mt-2 text-[12.5px]" />
              <div className="text-[11px] text-[#52525b] mt-1">{Math.round((p.size_bytes || 0)/1024)} KB · {new Date(p.uploaded_at).toLocaleString()}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Review ----------
function ReviewStep({ loc, readOnly, onEdit, onSubmit, submitting, error }) {
  const rows = [
    ["Business name", loc.business_name],
    ["Address", [loc.address_line1, loc.suite_unit, loc.address_line2].filter(Boolean).join(" · ")],
    ["City / State / ZIP", [loc.city, loc.state, loc.zip].filter(Boolean).join(", ")],
    ["Employees / Residents / Visitors", `${loc.employees ?? "—"} / ${loc.est_residents ?? "—"} / ${loc.visitors ?? "—"}`],
    ["Shifts / Hours / 24-7", `${loc.shifts ?? "—"} · ${loc.hours || "—"} · ${loc.open_24_7 ? "Yes" : "No"}`],
    ["Foot-traffic tier", loc.foot_traffic_tier ? loc.foot_traffic_tier.replace(/_/g, " ") : "—"],
    ["Existing vending", loc.vending_state ? loc.vending_state.replace(/_/g, " ") : "—"],
    ["Existing machines", (loc.existing_machine_types || []).join(", ")],
    ["DM interest", loc.dm_interest_level ? loc.dm_interest_level.replace(/_/g, " ") : "—"],
    ["Financial preference", loc.financial_pref ? loc.financial_pref.replace(/_/g, " ") : "—"],
    ["Requested %", loc.requested_commission_pct != null ? `${loc.requested_commission_pct}%` : "—"],
    ["Requested $/mo", loc.requested_monthly_amount != null ? `$${loc.requested_monthly_amount}` : "—"],
    ["Contract term", loc.preferred_term_months ? `${loc.preferred_term_months} months` : (loc.preferred_term_custom || "—")],
    ["Exclusivity", loc.exclusivity_pref ? loc.exclusivity_pref.replace(/_/g, " ") : "—"],
    ["Manager", loc.manager_name],
    ["Manager email", loc.manager_email],
  ];
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[16px] font-black">Review your submission</div>
        <div className="text-[12.5px] text-[#A1A1AA] mt-1">Please confirm before submitting.</div>
      </div>
      <div className="border border-[#2A2F34] rounded-lg p-4">
        <div className="grid sm:grid-cols-2 gap-3">
          {rows.map(([k, v]) => (
            <div key={k}>
              <div className="text-[10px] uppercase tracking-widest text-[#52525b]">{k}</div>
              <div className="mt-1 text-[13px] text-white/90 break-words">{v == null || v === "" ? <span className="text-[#52525b]">—</span> : String(v)}</div>
            </div>
          ))}
        </div>
        {!readOnly && (
          <button onClick={() => onEdit(0)} className="mt-4 text-[11px] font-bold uppercase tracking-widest text-[#FFC107] hover:underline flex items-center gap-1" data-testid="review-edit-all">
            <Edit3 size={11} /> Edit any section
          </button>
        )}
      </div>
      {error && <div className="text-[12px] text-[#EF4444]" data-testid="w-error">{error}</div>}
      {!readOnly && (
        <button disabled={submitting} onClick={onSubmit} data-testid="w-submit" className="sb-btn-primary w-full text-[12px]" style={{ padding: "14px 16px" }}>
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Submit Location
        </button>
      )}
    </div>
  );
}

// ---------- Primitives ----------
function F({ label, children }) {
  return <label className="block"><div className="sb-label">{label}</div>{children}</label>;
}
function Picker({ label, options, value, onChange, readOnly }) {
  return (
    <div>
      <div className="sb-label">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map(([v, l]) => {
          const active = value === v;
          return (
            <button key={v} type="button" disabled={readOnly} onClick={() => onChange(active ? null : v)}
                    className={`text-[11px] font-bold px-3 py-2 rounded-lg border transition-colors ${active ? "border-[#FFC107] text-[#FFC107] bg-[#FFC107]/10" : "border-[#2A2F34] text-[#A1A1AA] hover:text-white"}`}>{l}</button>
          );
        })}
      </div>
    </div>
  );
}
function MultiChip({ label, options, value, onChange, readOnly }) {
  return (
    <div>
      <div className="sb-label">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = value.includes(o);
          return (
            <button key={o} type="button" disabled={readOnly} onClick={() => onChange(active ? value.filter((x) => x !== o) : [...value, o])}
                    className={`text-[11px] font-bold px-3 py-2 rounded-lg border transition-colors ${active ? "border-[#FFC107] text-[#FFC107] bg-[#FFC107]/10" : "border-[#2A2F34] text-[#A1A1AA] hover:text-white"}`}>
              {o.replace(/_/g," ")}
            </button>
          );
        })}
      </div>
    </div>
  );
}
